import { IAssignedPlansResponseData } from '.';
import { IAbstractRecord } from '@models';
import { IListingData } from '@models/listing';
import PrecisionService from '@services/PrecisionService';
import {
  capitalizeFirstLetter,
  trimTrailingZeros,
  valueCommaSeparated,
} from '@utils/formatting-utils';

export function mapToAssignedPlanListingData(
  data: IAbstractRecord
): IListingData {
  const list: IAbstractRecord = {};

  data?.SalesforceManagement?.queries?.getBusinessPartnerPlanList?.items?.forEach(
    (obj: IAssignedPlansResponseData) => {
      list[obj.salesforceManagement_BusinessPartnerPlan_Id] = {
        salesforceManagement_BusinessPartnerPlan_LineID:
          obj.salesforceManagement_BusinessPartnerPlan_LineID?.id,
        salesforceManagement_BusinessPartnerPlan_PlanID:
          obj.salesforceManagement_BusinessPartnerPlan_PlanID?.id,
        salesforceManagement_BusinessPartnerPlan_BusinessPartnerLineID_Status:
          obj.salesforceManagement_BusinessPartnerPlan_BusinessPartnerLineID
            ?.views?.SalesforceManagement_all?.properties?.Status,
        salesforceManagement_BusinessPartnerPlan_BusinessPartnerLineID:
          obj.salesforceManagement_BusinessPartnerPlan_BusinessPartnerLineID
            ?.id,
        line_ExternalCode:
          obj.salesforceManagement_BusinessPartnerPlan_LineID?.views
            ?.PlanConfigManagement_all.properties?.ExternalCode,
        line_Name:
          obj.salesforceManagement_BusinessPartnerPlan_LineID?.views
            ?.PlanConfigManagement_all.properties?.Name,
        line_Status: capitalizeFirstLetter(
          obj.salesforceManagement_BusinessPartnerPlan_LineID?.views
            ?.PlanConfigManagement_all.properties?.Status
        ),

        salesforceManagement_BusinessPartnerPlan_BusinessPartnerLineID_LineCommissionType:
          obj.salesforceManagement_BusinessPartnerPlan_BusinessPartnerLineID
            ?.views?.SalesforceManagement_all?.properties?.LineCommissionType,
        salesforceManagement_BusinessPartnerPlan_BusinessPartnerLineID_LineCommissionValue:
          obj.salesforceManagement_BusinessPartnerPlan_BusinessPartnerLineID
            ?.views?.SalesforceManagement_all?.properties?.LineCommissionValue,
        salesforceManagement_BusinessPartnerPlan_BusinessPartnerLineID_ValueCurrency:
          obj.salesforceManagement_BusinessPartnerPlan_BusinessPartnerLineID
            ?.views?.SalesforceManagement_all?.properties?.ValueCurrency?.Code,
        salesforceManagement_BusinessPartnerPlan_BusinessPartnerLineID_LineCommissionRate:
          obj.salesforceManagement_BusinessPartnerPlan_BusinessPartnerLineID
            ?.views?.SalesforceManagement_all?.properties?.LineCommissionRate,
        salesforceManagement_BusinessPartnerPlan_BusinessPartnerLineID_ApplicableOn:
          obj.salesforceManagement_BusinessPartnerPlan_BusinessPartnerLineID
            ?.views?.SalesforceManagement_all?.properties?.ApplicableOn?.Code,

        salesforceManagement_BusinessPartnerPlan_Plan_SpecialCommissionApplicable:
          obj.salesforceManagement_BusinessPartnerPlan_PlanID?.views
            ?.PlanConfigManagement_all?.properties?.SpecialCommissionApplicable,
        salesforceManagement_BusinessPartnerPlan_Plan_SpecialCommissionType:
          capitalizeFirstLetter(
            obj.salesforceManagement_BusinessPartnerPlan_PlanID?.views
              ?.PlanConfigManagement_all?.properties?.SpecialCommissionType
          ),
        salesforceManagement_BusinessPartnerPlan_Plan_SpecialCommissionValue:
          obj.salesforceManagement_BusinessPartnerPlan_PlanID?.views
            ?.PlanConfigManagement_all?.properties?.SpecialCommissionValue,
        salesforceManagement_BusinessPartnerPlan_Plan_SpecialCommissionValueCurrency:
          obj.salesforceManagement_BusinessPartnerPlan_PlanID?.views
            ?.PlanConfigManagement_all?.properties
            ?.SpecialCommissionValueCurrency?.Code,
        salesforceManagement_BusinessPartnerPlan_Plan_SpecialCommissionRate:
          obj.salesforceManagement_BusinessPartnerPlan_PlanID?.views
            ?.PlanConfigManagement_all?.properties?.SpecialCommissionRate,
        salesforceManagement_BusinessPartnerPlan_Plan_SpecialCommissionApplicableOn:
          obj.salesforceManagement_BusinessPartnerPlan_PlanID?.views
            ?.PlanConfigManagement_all?.properties
            ?.SpecialCommissionApplicableOn?.Code,

        salesforceManagement_BusinessPartnerID:
          obj.salesforceManagement_BusinessPartnerPlan_BusinessPartnerID?.id,
        salesforceManagement_BusinessPartner_Status:
          obj.salesforceManagement_BusinessPartnerPlan_BusinessPartnerID?.views
            ?.SalesforceManagement_all?.properties?.Status,

        salesforceManagement_BusinessPartnerPlan_Id:
          obj.salesforceManagement_BusinessPartnerPlan_Id,
        salesforceManagement_BusinessPartnerPlan_LineName: obj.line_Name,
        plan_ExternalCode: obj.plan_ExternalCode,
        plan_Name: obj.plan_Name,
        plan_Status: capitalizeFirstLetter(obj.plan_PlanStatus),
        plan_PlanStatus: capitalizeFirstLetter(obj.plan_PlanStatus),
        salesforceManagement_BusinessPartnerPlan_PlanCommissionType:
          capitalizeFirstLetter(
            obj?.salesforceManagement_BusinessPartnerPlan_PlanCommissionType
          ),
        salesforceManagement_BusinessPartnerPlan_PlanCommissionValue_Only:
          obj.salesforceManagement_BusinessPartnerPlan_PlanCommissionValue,
        salesforceManagement_BusinessPartnerPlan_PlanCommissionValueCurrency_Only:
          obj.salesforceManagement_BusinessPartnerPlan_ValueCurrency?.Code,
        salesforceManagement_BusinessPartnerPlan_PlanCommissionRate_Only:
          obj.salesforceManagement_BusinessPartnerPlan_PlanCommissionRate,

        salesforceManagement_BusinessPartnerPlan_PlanCommissionValue:
          obj.salesforceManagement_BusinessPartnerPlan_PlanCommissionType ===
          'VALUE'
            ? obj.salesforceManagement_BusinessPartnerPlan_PlanCommissionValue !==
              null
              ? `${
                  obj.salesforceManagement_BusinessPartnerPlan_ValueCurrency
                    ?.Symbol
                } ${trimTrailingZeros(
                  valueCommaSeparated(
                    obj.salesforceManagement_BusinessPartnerPlan_PlanCommissionValue
                  ),
                  3,
                  true
                )}`
              : ''
            : obj.salesforceManagement_BusinessPartnerPlan_PlanCommissionRate !==
                null
              ? `${trimTrailingZeros(
                  PrecisionService.multiplyBy100(
                    Number(
                      obj.salesforceManagement_BusinessPartnerPlan_PlanCommissionRate
                    )
                  )
                )}%`
              : '',
        salesforceManagement_BusinessPartnerPlan_ApplicableOnCode:
          obj?.salesforceManagement_BusinessPartnerPlan_ApplicableOn?.Code,
        salesforceManagement_BusinessPartnerPlan_ApplicableOn:
          obj?.salesforceManagement_BusinessPartnerPlan_ApplicableOn?.Title,
        salesforceManagement_BusinessPartnerPlan_ActiveFrom:
          obj.salesforceManagement_BusinessPartnerPlan_ActiveFrom,
        salesforceManagement_BusinessPartnerPlan_ActiveTo:
          obj.salesforceManagement_BusinessPartnerPlan_ActiveTo,
        salesforceManagement_BusinessPartnerPlan_Status: capitalizeFirstLetter(
          obj.salesforceManagement_BusinessPartnerPlan_Status?.replaceAll(
            '_',
            ' '
          )
        ),
        AvailableActions:
          obj.salesforceManagement_BusinessPartnerPlan_Status.toLowerCase() ===
            'active' &&
          obj.salesforceManagement_BusinessPartnerPlan_BusinessPartnerLineID?.views?.SalesforceManagement_all?.properties?.Status.toLowerCase() ===
            'active' &&
          obj.line_Status.toLowerCase() === 'active' &&
          obj.plan_PlanStatus.toLowerCase() === 'active'
            ? ['setCommission', 'deactivate']
            : ['activatePlans'],
      };
    }
  );

  return {
    pagedItems: list,
    pageSize:
      data?.SalesforceManagement?.queries?.getBusinessPartnerPlanList?.paging
        ?.pageSize,
    pageNumber:
      data?.SalesforceManagement?.queries?.getBusinessPartnerPlanList?.paging
        ?.pageNumber,
    totalCount:
      data?.SalesforceManagement?.queries?.getBusinessPartnerPlanList?.paging
        ?.totalCount,
  };
}

export function extractAssignedBP(
  data: IAbstractRecord
): Record<string, Record<string, string>> {
  const lovs: Record<string, Record<string, string>> = {
    line: {},
  };

  data?.SalesforceManagement?.queries?.getBusinessPartnerLinesListWithoutPagination?.forEach(
    (obj: {
      salesforceManagement_BusinessPartnerLine_Id: string;
      salesforceManagement_BusinessPartnerLine_LineID: {
        id: string;
        views: {
          PlanConfigManagement_all: {
            properties: {
              Name: string;
              ExternalCode: string;
            };
          };
        };
      };
    }) => {
      lovs.line[obj?.salesforceManagement_BusinessPartnerLine_Id] =
        `${obj?.salesforceManagement_BusinessPartnerLine_LineID?.views?.PlanConfigManagement_all?.properties?.ExternalCode} - ${obj?.salesforceManagement_BusinessPartnerLine_LineID?.views?.PlanConfigManagement_all?.properties?.Name}`;
    }
  );

  return lovs;
}
